import React, { useMemo, useRef, useState } from "react";

const FALLBACK_CURRENCY_CODES = [
  "INR",
  "USD",
  "EUR",
  "GBP",
  "AED",
  "AUD",
  "BDT",
  "CAD",
  "CHF",
  "CNY",
  "HKD",
  "IDR",
  "JPY",
  "KES",
  "KRW",
  "KWD",
  "LKR",
  "MAD",
  "MXN",
  "MYR",
  "NGN",
  "NOK",
  "NPR",
  "NZD",
  "OMR",
  "PHP",
  "PKR",
  "QAR",
  "RUB",
  "SAR",
  "SEK",
  "SGD",
  "THB",
  "TRY",
  "UGX",
  "VND",
  "ZAR",
];

function getDisplayNames() {
  if (typeof Intl === "undefined" || typeof Intl.DisplayNames !== "function") return null;
  try {
    return new Intl.DisplayNames(["en"], { type: "currency" });
  } catch {
    return null;
  }
}

function getSupportedCurrencyCodes() {
  const fallback = [...new Set(FALLBACK_CURRENCY_CODES)];
  if (typeof Intl === "undefined" || typeof Intl.supportedValuesOf !== "function") {
    return fallback;
  }

  try {
    const codes = Intl.supportedValuesOf("currency")
      .map((value) => String(value || "").trim().toUpperCase())
      .filter((value) => /^[A-Z]{3}$/.test(value));
    return [...new Set(["INR", ...codes, ...fallback])];
  } catch {
    return fallback;
  }
}

function normalizeCurrencyCode(value) {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : "";
}

function getCurrencySymbol(code) {
  const normalizedCode = normalizeCurrencyCode(code);
  if (!normalizedCode || typeof Intl === "undefined" || typeof Intl.NumberFormat !== "function") {
    return normalizedCode;
  }

  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency: normalizedCode,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    const symbol = parts.find((part) => part.type === "currency")?.value || normalizedCode;
    return symbol.toUpperCase() === normalizedCode ? normalizedCode : symbol;
  } catch {
    return normalizedCode;
  }
}

function buildCurrencyOptions() {
  const displayNames = getDisplayNames();
  return getSupportedCurrencyCodes()
    .map((code) => {
      const name = displayNames?.of(code) || code;
      const symbol = getCurrencySymbol(code) || code;
      return {
        id: code,
        code,
        name,
        symbol,
        searchText: `${code} ${name} ${symbol}`.toLowerCase(),
      };
    })
    .sort((left, right) => {
      if (left.code === "INR") return -1;
      if (right.code === "INR") return 1;
      return left.name.localeCompare(right.name) || left.code.localeCompare(right.code);
    });
}

const CURRENCY_OPTIONS = buildCurrencyOptions();

export { CURRENCY_OPTIONS };

export function getCurrencyOption(value) {
  const code = normalizeCurrencyCode(value);
  if (!code) return null;
  return (
    CURRENCY_OPTIONS.find((option) => option.code === code) || {
      id: code,
      code,
      name: code,
      symbol: getCurrencySymbol(code) || code,
      searchText: code.toLowerCase(),
    }
  );
}

export function getCurrencyName(value) {
  return getCurrencyOption(value)?.name || "";
}

export function formatCurrencyAmount(amount, currencyCode = "INR", locale = "en-IN") {
  if (amount === null || amount === undefined || amount === "") return "";
  const code = normalizeCurrencyCode(currencyCode) || "INR";
  const number = Number(amount);

  if (!Number.isFinite(number)) {
    return String(amount);
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 0,
    }).format(number);
  } catch {
    const option = getCurrencyOption(code);
    return `${option?.symbol || code} ${number.toLocaleString("en-IN")}`.trim();
  }
}

export function normalizeOccupationCurrencyCode(value, amount) {
  if (amount === null || amount === undefined || amount === "") return null;
  return normalizeCurrencyCode(value) || "INR";
}

export function CurrencySelect({
  value,
  onChange,
  placeholder = "Select currency",
  disabled = false,
}) {
  const [query, setQuery] = useState("");
  const detailsRef = useRef(null);
  const selectedOption = getCurrencyOption(value);
  const normalizedQuery = query.trim().toLowerCase();

  const visibleOptions = useMemo(() => {
    if (!normalizedQuery) return CURRENCY_OPTIONS;
    return CURRENCY_OPTIONS.filter((option) => option.searchText.includes(normalizedQuery));
  }, [normalizedQuery]);

  return (
    <details ref={detailsRef} style={{ ...styles.currencyDropdown, ...(disabled ? styles.currencyDropdownDisabled : {}) }}>
      <summary style={{ ...styles.currencySummary, ...(disabled ? styles.currencySummaryDisabled : {}) }}>
        {selectedOption ? (
          <span style={styles.currencySummaryLabel}>
            <span style={styles.currencyBadge}>{selectedOption.symbol}</span>
            <span>{selectedOption.code} - {selectedOption.name}</span>
          </span>
        ) : (
          <span style={styles.currencyPlaceholder}>{placeholder}</span>
        )}
      </summary>

      <div style={styles.currencyMenu}>
        {!disabled && (
          <input
            type="text"
            style={styles.currencySearch}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search currency..."
          />
        )}

        <div style={styles.currencyOptions}>
          {disabled ? (
            <div style={styles.currencyEmpty}>{placeholder}</div>
          ) : visibleOptions.length === 0 ? (
            <div style={styles.currencyEmpty}>No currencies found.</div>
          ) : (
            visibleOptions.map((option) => {
              const active = option.code === selectedOption?.code;
              return (
                <button
                  key={option.code}
                  type="button"
                  style={{ ...styles.currencyOption, ...(active ? styles.currencyOptionActive : {}) }}
                  onClick={() => {
                    onChange?.(option.code);
                    setQuery("");
                    if (detailsRef.current) detailsRef.current.open = false;
                  }}
                >
                  <span style={styles.currencyOptionMain}>
                    <span style={styles.currencyBadge}>{option.symbol}</span>
                    <span>{option.name}</span>
                  </span>
                  <span style={styles.currencyCode}>{option.code}</span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </details>
  );
}

export function CurrencyAmountInput({
  amount,
  currencyCode,
  onAmountChange,
  onCurrencyChange,
  placeholder,
  disabled = false,
}) {
  return (
    <div style={styles.amountRow}>
      <div style={styles.amountCurrencySlot}>
        <CurrencySelect
          value={currencyCode || "INR"}
          onChange={onCurrencyChange}
          placeholder="Currency"
          disabled={disabled}
        />
      </div>
      <input
        type="number"
        style={{ ...styles.amountInput, ...(disabled ? styles.amountInputDisabled : {}) }}
        value={amount || ""}
        onChange={(event) => onAmountChange?.(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
}

const styles = {
  amountRow: {
    display: "flex",
    flexWrap: "nowrap",
    gap: 12,
    alignItems: "stretch",
  },
  amountInput: {
    flex: "1 1 auto",
    minWidth: 0,
    width: "100%",
    borderRadius: 18,
    border: "1px solid #f2c7c0",
    padding: "14px 16px",
    fontSize: 16,
    outline: "none",
    background: "#fff",
    color: "#2b3142",
  },
  amountInputDisabled: {
    background: "#f7f4f2",
    color: "#7f8795",
    cursor: "not-allowed",
  },
  amountCurrencySlot: {
    flex: "0 0 230px",
    minWidth: 0,
  },
  currencyDropdown: {
    width: "100%",
    position: "relative",
  },
  currencyDropdownDisabled: {
    opacity: 0.8,
  },
  currencySummary: {
    listStyle: "none",
    borderRadius: 18,
    border: "1px solid #f2c7c0",
    padding: "10px 14px",
    background: "#fff",
    cursor: "pointer",
    color: "#2b3142",
    minHeight: 46,
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
  },
  currencySummaryDisabled: {
    cursor: "not-allowed",
    background: "#f7f4f2",
  },
  currencySummaryLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 600,
    minWidth: 0,
    width: "100%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  currencyPlaceholder: {
    color: "#7f8795",
    fontSize: 13,
    fontWeight: 500,
  },
  currencyBadge: {
    minWidth: 32,
    height: 24,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff3ef",
    color: "#cf3a30",
    border: "1px solid #f2c7c0",
    fontSize: 13,
    fontWeight: 700,
    padding: "0 8px",
    boxSizing: "border-box",
    flexShrink: 0,
  },
  currencyMenu: {
    marginTop: 8,
    borderRadius: 20,
    border: "1px solid #f2c7c0",
    background: "#fffdfc",
    boxShadow: "0 16px 34px rgba(207,58,48,0.08)",
    padding: 12,
    position: "absolute",
    width: "100%",
    zIndex: 40,
  },
  currencySearch: {
    width: "100%",
    borderRadius: 14,
    border: "1px solid #f2c7c0",
    padding: "12px 14px",
    fontSize: 14,
    outline: "none",
    marginBottom: 10,
  },
  currencyOptions: {
    maxHeight: 280,
    overflowY: "auto",
    display: "grid",
    gap: 8,
  },
  currencyOption: {
    width: "100%",
    border: "1px solid #f1d6d1",
    borderRadius: 16,
    background: "#fff",
    padding: "11px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    cursor: "pointer",
    textAlign: "left",
    color: "#2b3142",
  },
  currencyOptionActive: {
    borderColor: "#cf3a30",
    background: "#fff5f2",
  },
  currencyOptionMain: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },
  currencyCode: {
    color: "#7f8795",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
  },
  currencyEmpty: {
    padding: "12px 10px",
    color: "#7f8795",
    fontSize: 14,
  },
};
